import { ReactNode } from "react";

import LinkButton from "common/components/LinkButton";
import { pluralized } from "common/utils/strings";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useDeleteAddressMutation } from "client/accounts/hooks";
import Alert from "client/common/components/content/Alert";
import { urls } from "client/routes";
import { IClientProfileListAddress } from "client/accounts/types";

import styles from "../../styles/RemoveAddressDrawer.module.less";

export type RemoveAddressDrawerProps = {
  id: number;
  address: IClientProfileListAddress;
  addressGroup: ReactNode;
  isCurrentlyPrimary: boolean;
  onSuccess: () => void;
};

const RemoveAddressDrawer = ({
  id,
  address,
  addressGroup,
  isCurrentlyPrimary,
  onSuccess,
}: RemoveAddressDrawerProps) => {
  const { close } = useCurrentDrawer();
  const handleSuccess = () => {
    onSuccess();
    close();
  };
  const {
    mutate: deleteAddress,
    isPending: isDeleting,
    error: deleteAddressError,
  } = useDeleteAddressMutation(id, { onSuccess: handleSuccess });

  const getAutoshipsLink = (autoshipCount: number) => {
    return (
      <LinkButton className={styles.link} dest={urls.subscriptions.list()}>
        {pluralized(autoshipCount, "Autoship")}
      </LinkButton>
    );
  };

  const hasActiveSubscriptions = !!address.active_subscription_count;
  const hasPausedSubscriptions = !!address.paused_subscription_count;

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Remove address" onClose={close} />
      <DrawerLayout.Form
        canSubmit={true}
        onSubmit={() => deleteAddress()}
        isLoading={isDeleting}
        submitText="Remove Address"
        error={deleteAddressError}
      >
        {addressGroup}
        {(hasActiveSubscriptions || hasPausedSubscriptions) && (
          <DrawerLayout.Form.Group>
            {hasActiveSubscriptions && (
              <Alert>
                {`Address is used for `}
                <span className={styles.subscriptionCount}>
                  {`${address.active_subscription_count} active `}
                  {getAutoshipsLink(address.active_subscription_count)}
                </span>
                {` that will automatically pause if address is removed.`}
              </Alert>
            )}
            {hasPausedSubscriptions && (
              <Alert>
                {`Address is used for `}
                <span className={styles.subscriptionCount}>
                  {`${address.paused_subscription_count} paused `}
                  {getAutoshipsLink(address.paused_subscription_count)}
                </span>
                {` that will require an update before resuming if address is removed.`}
              </Alert>
            )}
          </DrawerLayout.Form.Group>
        )}
        {isCurrentlyPrimary && (
          <DrawerLayout.Form.Group>
            <p className={styles.label}>This will permanently remove this address.</p>
            <p className={styles.label}>
              NOTE: In-progress orders placed with this address will still be shipped here. Please
              contact customer support for assistance on in-progress orders.
            </p>
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default RemoveAddressDrawer;
